// src/lib/cruzamentoGoogleListaFria.ts
//
// Casa uma empresa achada no Google Places com o CNPJ da mesma empresa na Lista Fria.
//
// Por que existe: o Google não traz CNPJ (descobrir custa uma consulta na Casa dos Dados)
// e as duas bases deixavam importar a mesma empresa duas vezes, uma por cada lado.
//
// Medido em 25/10/2026 sobre o pool inteiro: das 4.935 empresas do Google, ~150 (3%)
// casam com segurança. As bases cobrem empresas diferentes, então o cruzamento serve para
// evitar duplicata e dar o CNPJ de graça — não para descobrir CNPJ em massa.
//
// Regra (calibrada olhando os pares, ver a nota no fim):
//  - mesmo telefone (8 últimos dígitos) a até 300 m; ou
//  - até 100 m e nome parecido: as palavras significativas em comum cobrem pelo menos 60%
//    das palavras do nome mais curto. Palavra genérica ("Empório", "Motel", "Oeste", nome
//    de cidade) não conta — sozinha ela casava empresas vizinhas diferentes.

export interface PontoGoogle {
  place_id: string;
  nome: string;
  telefone?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface PontoListaFria {
  place_id: string;
  nome: string;
  nome_fantasia?: string | null;
  razao_social?: string | null;
  telefone?: string | null;
  latitude: number;
  longitude: number;
}

export interface Casamento<T extends PontoListaFria = PontoListaFria> {
  listaFria: T;
  via: 'telefone' | 'nome';
  metros: number;
}

const GENERICAS = new Set([
  'ltda', 'eireli', 'me', 'epp', 'comercio', 'comercial', 'servicos', 'servico', 'empresa', 'grupo', 'industria',
  'mercado', 'supermercado', 'minimercado', 'mercearia', 'atacado', 'atacadista', 'distribuidora', 'distribuidor',
  'restaurante', 'lanchonete', 'lanches', 'bar', 'boteco', 'espetinho', 'pizzaria', 'sorveteria', 'padaria',
  'panificadora', 'confeitaria', 'acougue', 'hortifruti', 'sacolao', 'verdurao', 'conveniencia', 'emporio',
  'bebidas', 'bebida', 'alimentos', 'produtos', 'farmacia', 'farmacias', 'drogaria', 'drogarias', 'posto', 'clinica',
  'medica', 'odontologica', 'escola', 'colegio', 'hotel', 'motel', 'pousada', 'academia', 'fitness', 'fitnes',
  'loja', 'casa', 'center', 'centro', 'store', 'shop', 'express', 'delivery', 'prime',
  'goiania', 'goias', 'brasil', 'aparecida', 'anapolis', 'senador', 'canedo', 'trindade', 'goianira', 'itumbiara',
  'setor', 'jardim', 'vila', 'parque', 'norte', 'sul', 'leste', 'oeste', 'noroeste', 'sudoeste', 'nordeste', 'sudeste',
]);

const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Palavras que identificam a empresa (4+ letras, sem as genéricas). */
export function palavrasDoNome(...nomes: Array<string | null | undefined>): Set<string> {
  const out = new Set<string>();
  for (const n of nomes) {
    for (const w of semAcento(String(n || '')).split(/[^a-z0-9]+/)) {
      if (w.length >= 4 && !GENERICAS.has(w)) out.add(w);
    }
  }
  return out;
}

const fone8 = (s?: string | null) => String(s || '').replace(/\D/g, '').slice(-8);

export function metrosEntre(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371000, t = Math.PI / 180;
  const dLat = (b.lat - a.lat) * t, dLng = (b.lng - a.lng) * t;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * t) * Math.cos(b.lat * t) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Nome parecido: as palavras em comum cobrem 60%+ do nome mais curto. */
export function nomesParecidos(a: Set<string>, b: Set<string>): boolean {
  if (a.size === 0 || b.size === 0) return false;
  let comum = 0;
  a.forEach((w) => { if (b.has(w)) comum++; });
  return comum >= 1 && comum / Math.min(a.size, b.size) >= 0.6;
}

/**
 * Para cada empresa do Google, a linha da Lista Fria que é a mesma empresa (ou nada).
 * Telefone vence nome; entre candidatos do mesmo tipo, o mais perto.
 */
export function cruzarGoogleComListaFria<T extends PontoListaFria>(
  google: PontoGoogle[],
  listaFria: T[],
): Map<string, Casamento<T>> {
  // Grade de ~1 km: só compara com quem está nas células vizinhas
  const cel = (lat: number, lng: number) => `${Math.floor(lat * 100)}|${Math.floor(lng * 100)}`;
  const grade = new Map<string, Array<{ lf: T; palavras: Set<string>; fone: string }>>();
  for (const lf of listaFria) {
    if (lf.latitude == null || lf.longitude == null) continue;
    const k = cel(lf.latitude, lf.longitude);
    if (!grade.has(k)) grade.set(k, []);
    grade.get(k)!.push({ lf, palavras: palavrasDoNome(lf.nome, lf.nome_fantasia, lf.razao_social), fone: fone8(lf.telefone) });
  }

  const out = new Map<string, Casamento<T>>();
  for (const g of google) {
    if (g.latitude == null || g.longitude == null) continue;
    const p = { lat: g.latitude, lng: g.longitude };
    const palavras = palavrasDoNome(g.nome);
    const fone = fone8(g.telefone);
    const la = Math.floor(g.latitude * 100), ln = Math.floor(g.longitude * 100);
    let melhor: Casamento<T> | null = null;
    for (let i = -1; i <= 1; i++) {
      for (let j = -1; j <= 1; j++) {
        for (const c of grade.get(`${la + i}|${ln + j}`) || []) {
          const metros = metrosEntre(p, { lat: c.lf.latitude, lng: c.lf.longitude });
          const via: Casamento['via'] | null =
            fone.length === 8 && c.fone === fone && metros <= 300 ? 'telefone'
              : metros <= 100 && nomesParecidos(palavras, c.palavras) ? 'nome'
                : null;
          if (!via) continue;
          const ganha = !melhor
            || (via === 'telefone' && melhor.via === 'nome')
            || (via === melhor.via && metros < melhor.metros);
          if (ganha) melhor = { listaFria: c.lf, via, metros: Math.round(metros) };
        }
      }
    }
    if (melhor) out.set(g.place_id, melhor);
  }
  return out;
}

// Calibragem (25/10/2026): com "1 palavra em comum a 150 m" saíam 342 pares e boa parte
// errada ("Status Motel ⇄ Eros Motel", "Varandinha Espetos Oeste ⇄ Oft Plaza Oeste
// Hotel"). Com 60% das palavras, 100 m e as genéricas fora, sobram ~150, e os pares
// conferidos à mão são a mesma empresa ("Empório e Sacolão Moreira ⇄ Emporio e Sacolao
// Moreira", "Ponto de Apoio Bebidas e Tabacaria" pelo telefone).
